/**
 * News Card Component
 * Displays a single news card with image, title, category, and link
 * Uses GraphQL Codegen types for type safety
 * Style adapted from article-card.tsx
 */

'use client';

import { highlightText } from '@/utils/highlightText';
import { ArrowRight, Calendar, Tag } from 'lucide-react';
import Image from 'next/image';
import Link from 'next/link';
import type { NewsTeaserType } from '../../types/news';

interface NewsCardProps {
  news: NewsTeaserType;
  baseUrl?: string; // Base URL for news links (e.g., '/news')
  searchTerm?: string; // Search term for highlighting (optional)
}

/**
 * Format date from timestamp
 */
function formatDate(timestamp: number | null | undefined): string | null {
  if (!timestamp) {
    return null;
  }

  const date = new Date(timestamp * 1000);
  if (Number.isNaN(date.getTime())) {
    return null;
  }

  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
}

/**
 * Get image URL from news item
 * Priority:
 * 1. Extract <img src="..."> from contentNews (field_content_news)
 * 2. Try imageUrl (field_image_url) if available
 * 3. Try imageNews (field_image_news) media entity
 */
function getImageUrl(news: NewsTeaserType): string | null {
  // Priority 1: Extract image from contentNews (field_content_news)
  // This field contains HTML with <img> tags
  const contentNewsHtml = news.contentNews?.processed || news.contentNews?.value;
  if (contentNewsHtml) {
    // Extract src from <img> tag
    const imgMatch = contentNewsHtml.match(/<img[^>]+src=["']([^"']+)["']/i);
    if (imgMatch && imgMatch[1] && imgMatch[1].trim()) {
      const extractedUrl = imgMatch[1].trim();
      // If extracted URL is absolute, use it directly
      if (/^https?:\/\//i.test(extractedUrl)) {
        return extractedUrl;
      }
      // Otherwise, treat as relative path
      return extractedUrl;
    }
  }
  
  // Priority 2: Try imageUrl (field_image_url) - Text field
  const imageUrlRaw = news.imageUrl?.processed || news.imageUrl?.value;
  if (imageUrlRaw && imageUrlRaw.trim()) {
    const htmlString = imageUrlRaw.trim();
    
    // If it contains HTML img tag, extract src attribute
    if (htmlString.includes('<img')) {
      const srcMatch = htmlString.match(/src=["']([^"']+)["']/i);
      if (srcMatch && srcMatch[1] && srcMatch[1].trim()) {
        const extractedUrl = srcMatch[1].trim();
        if (/^https?:\/\//i.test(extractedUrl)) {
          return extractedUrl;
        }
        return extractedUrl;
      }
    }
    
    // If it's already an absolute URL, use it directly
    if (/^https?:\/\//i.test(htmlString)) {
      return htmlString;
    }
    
    // Otherwise, treat as relative path
    return htmlString;
  }
  
  // Priority 3: Try imageNews (field_image_news) - Media entity
  if (news.imageNews?.__typename === 'MediaImage' && news.imageNews.mediaImage?.url) {
    return news.imageNews.mediaImage.url;
  }
  
  return null;
}

/**
 * Get link URL from news item
 * Priority: sourceLink > path > null
 */
function getLinkUrl(news: NewsTeaserType): string | null {
  if (news.sourceLink?.url) {
    return news.sourceLink.url;
  }
  
  if (news.path) {
    return news.path;
  }
  
  return null;
}

/**
 * Extract text excerpt from HTML
 */
function getTextExcerpt(html: string | null | undefined, maxLength = 150): string {
  if (!html) return '';
  const text = html.replace(/<[^>]*>/g, '');
  return text.length > maxLength ? text.slice(0, maxLength) + '...' : text;
}

export function NewsCard({ news, baseUrl = '/news', searchTerm }: NewsCardProps) {
  const title = news.title || 'News';
  
  const imageUrl = getImageUrl(news);
  const linkUrl = getLinkUrl(news);
  const categories = news.category || [];
  const createdDate = formatDate(news.created?.timestamp);
  
  // Get excerpt from body summary, processed, or contentNews
  const excerpt = getTextExcerpt(
    news.body?.summary || news.body?.processed || news.contentNews?.processed || null
  );
  
  // If sourceLink exists, always open in new tab
  const hasSourceLink = !!news.sourceLink?.url;
  
  // Construct news URL
  const newsUrl = linkUrl 
    ? (hasSourceLink ? linkUrl : `${baseUrl}${linkUrl}`)
    : null;

  // Helper for highlighting text
  const safeHighlightText = (text?: string | null) => {
    if (!text) return '';
    if (!searchTerm) return text;
    return highlightText(text, searchTerm);
  };

  // Handle click for external links
  const handleCardClick = (e: React.MouseEvent) => {
    if (hasSourceLink && linkUrl) {
      e.preventDefault();
      window.open(linkUrl, '_blank', 'noopener,noreferrer');
    }
  };

  const cardContent = (
    <>
      {/* Image */}
      
      {imageUrl && (
        <div className="relative aspect-video overflow-hidden">
          
          <Image
            src={imageUrl}
            alt={title}
            fill
            className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-105"
          />
        </div>
      )}

      {/* Content */}
      <div className="flex flex-1 flex-col p-4">
        {/* Sticky badge */}
        {news.sticky && (
          <span className="mb-2 w-fit rounded bg-primary/10 px-2 py-1 text-xs font-medium text-primary">
            Featured
          </span>
        )}

        <h3 className="mb-2 line-clamp-2 text-lg font-semibold leading-tight transition-colors group-hover:text-primary">
          {safeHighlightText(title)}
        </h3>

        {/* Meta */}
        <div className="mb-3 flex flex-wrap items-center gap-2 text-sm text-muted-foreground">
          {createdDate && (
            <>
              <Calendar className="h-3 w-3" />
              <time dateTime={new Date((news.created?.timestamp || 0) * 1000).toISOString()}>
                {createdDate}
              </time>
            </>
          )}

          {categories.length > 0 && (
            <div className="hidden">
              {createdDate && <span>•</span>}
              <Tag className="h-3 w-3" />
              <div className="flex flex-wrap items-center gap-1">
                {categories.map((cat, index) => (
                  <span key={cat.id || index}>
                    {index > 0 && <span className="text-muted-foreground">, </span>}
                    {safeHighlightText(cat.name)}
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Excerpt */}
        {excerpt && (
          <p className="line-clamp-3 flex-1 text-sm text-muted-foreground">
            {safeHighlightText(excerpt)}
          </p>
        )}
<div className="flex items-center gap-2 text-sm font-medium text-primary group-hover:gap-3 transition-all mt-auto pt-6">
                      Read more
                      <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
                    </div>
      </div>
    </>
  );

  // If sourceLink exists, card opens in new tab (don't use Next.js Link)
  if (hasSourceLink && linkUrl) {
    return (
      <div
        onClick={handleCardClick}
        className="group flex flex-col  h-full cursor-pointer overflow-hidden rounded bg-slate-500/5 shadow-sm transition-all hover:shadow-lg"
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            handleCardClick(e as any);
          }
        }}
      >
        {cardContent}
      </div>
    );
  }

  // For internal links, use Next.js Link
  if (newsUrl && !hasSourceLink) {
    return (
      <Link
        href={newsUrl}
        className="group block h-full overflow-hidden rounded bg-slate-500/5 shadow-sm transition-all hover:shadow-lg"
      >
        {cardContent}
      </Link>
    );
  }

  // No link - just display card
  return (
    <div className="group block h-full overflow-hidden rounded bg-slate-500/5 shadow-sm transition-all hover:shadow-lg">
      {cardContent}
    </div>
  );
}
